import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PiCheck } from 'react-icons/pi';
import BottomSheet from '../BottomSheet';
import { SelectField, TextField } from '../FormFields';
import { CATEGORY_TYPES } from '../../domain/finance';
import { CATEGORY_NAME_MAX_LENGTH } from '../../services/categoryService';
import {
  CATEGORY_COLOR_OPTIONS,
  CATEGORY_ICON_COMPONENTS,
  CATEGORY_ICON_OPTIONS,
  getCategoryColor,
  isCategoryColorId,
  isCategoryIconId,
} from '../../utils/categoryPresentation';
import './styles.css';

import './picker.css';
const ERROR_MESSAGES = {
  CATEGORY_NAME_REQUIRED: 'Informe um nome para a categoria.',
  CATEGORY_NAME_TOO_LONG: `Use no máximo ${CATEGORY_NAME_MAX_LENGTH} caracteres.`,
  CATEGORY_DUPLICATE: 'Já existe uma categoria com esse nome nesta conta.',
  INVALID_CATEGORY_TYPE: 'Selecione um tipo válido.',
};

const CategoryFormSheet = ({ category, onSave, onClose }) => {
  const [name, setName] = useState(category?.name || '');
  const [type, setType] = useState(category?.type || CATEGORY_TYPES.EXPENSE);
  const [icon, setIcon] = useState(() => isCategoryIconId(category?.icon) ? category.icon : 'tag');
  const [colorToken, setColorToken] = useState(() => isCategoryColorId(category?.colorToken) ? category.colorToken : 'default');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setName(category?.name || '');
    setType(category?.type || CATEGORY_TYPES.EXPENSE);
    setIcon(isCategoryIconId(category?.icon) ? category.icon : 'tag');
    setColorToken(isCategoryColorId(category?.colorToken) ? category.colorToken : 'default');
  }, [category]);

  const submit = async (event) => {
    event?.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave({ name, type, icon, colorToken });
      onClose();
    } catch (saveError) {
      setError(ERROR_MESSAGES[saveError.message] || 'Não foi possível salvar a categoria. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const PreviewIcon = CATEGORY_ICON_COMPONENTS[icon] || CATEGORY_ICON_COMPONENTS.tag;
  const previewColor = getCategoryColor(colorToken);
  return (
    <BottomSheet
      title={category ? 'Editar categoria' : 'Nova categoria'}
      subtitle="A categoria pertence somente à conta financeira atual."
      onClose={onClose}
      size="sm"
      footer={<button type="submit" form="category-form" className="nf-sheet__primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar categoria'}</button>}
      className="category-form-sheet"
    >
      <form id="category-form" className="category-form" onSubmit={submit}>
        <TextField id="category-name" label="Nome" value={name} onChange={setName} maxLength={CATEGORY_NAME_MAX_LENGTH} required />
        <SelectField id="category-type" label="Tipo" value={type} onChange={setType} required>
          <option value={CATEGORY_TYPES.EXPENSE}>Despesa</option>
          <option value={CATEGORY_TYPES.INCOME}>Receita</option>
          <option value={CATEGORY_TYPES.BOTH}>Receita e despesa</option>
        </SelectField>
        <section className="category-form__preview" aria-label="Prévia da categoria">
          <span className="category-form__preview-icon" style={{ '--category-color': previewColor }} aria-hidden="true"><PreviewIcon /></span>
          <span><strong>{name.trim() || 'Nova categoria'}</strong><small>{type === CATEGORY_TYPES.INCOME ? 'Receita' : type === CATEGORY_TYPES.BOTH ? 'Receita e despesa' : 'Despesa'}</small></span>
        </section>

        <fieldset className="category-form__section">
          <legend>Ícone</legend>
          <div className="category-form__icon-grid" role="group" aria-label="Escolha um ícone">
            {CATEGORY_ICON_OPTIONS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                className={icon === id ? 'is-selected' : ''}
                onClick={() => setIcon(id)}
                aria-label={label}
                aria-pressed={icon === id}
              >
                <Icon aria-hidden="true" />
                {icon === id && <PiCheck className="category-form__selected-check" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="category-form__section">
          <legend>Cor</legend>
          <div className="category-form__color-grid" role="group" aria-label="Escolha uma cor">
            {CATEGORY_COLOR_OPTIONS.map(({ id, label, cssColor }) => (
              <button
                key={id}
                type="button"
                className={colorToken === id ? 'is-selected' : ''}
                onClick={() => setColorToken(id)}
                aria-label={label}
                aria-pressed={colorToken === id}
              >
                <span style={{ '--swatch-color': cssColor }} aria-hidden="true" />
                <small>{label}</small>
                {colorToken === id && <PiCheck aria-hidden="true" />}
              </button>
            ))}
          </div>
        </fieldset>
        {error && <p className="category-form__error" role="alert">{error}</p>}
      </form>
    </BottomSheet>
  );
};

CategoryFormSheet.propTypes = {
  category: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CategoryFormSheet;
